/** The tour-operator setup wizard — a full-fledged page, not a one-shot dialog.
 *
 *  Two modes on one shell:
 *    /tour-operators/new/advanced    — create mode (also /tour-operators/new when
 *                                      easy onboarding cannot run): the three creation steps
 *                                      (local form state, one POST), then a
 *                                      handoff to…
 *    /tour-operators/:id/setup/:step? — setup mode: a live, URL-addressable view
 *                                      over the preflight checklist (profile →
 *                                      parameters → item steps → run board).
 *
 *  Setup mode exists only for tenants this wizard created (`onboarding_managed`);
 *  any other operator's /setup URL renders a short notice instead of a wizard,
 *  because there is no checklist behind it.
 *
 *  Setup mode holds NO client wizard state: statuses derive from the items
 *  query on every poll, so ops can leave a half-configured tenant (syncs run
 *  for hours, human items for days) and return to the same URL. The left rail
 *  is a status-glyph nav list (see SetupRail); the browser back/forward moves
 *  between steps because the step IS the URL.
 */
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import type { PreflightItem } from '@/api/preflight';
import { sandboxErrorMessage } from '@/api/sandbox';
import * as topsApi from '@/api/tourOperators';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { Step1BasicInfo } from '../wizard/Step1BasicInfo';
import { Step2AdminUser } from '../wizard/Step2AdminUser';
import { StepProfile } from '../wizard/StepProfile';
import { cleanTeamMembers } from '../wizard/teamMembers';
import { createPayload, initialWizardData, type WizardData } from '../wizard/types';
import { EnvironmentBadge } from '../sandbox/EnvironmentBadge';
import { PairedSummary, WizardSandboxSection } from '../sandbox/WizardSandboxSection';
import { prefillFromState } from '../onboard/advancedLinks';
import { usePreflightItemActions } from '../tabs/usePreflightItemActions';
import { isTenantCode, TENANT_CODE_RULE } from '../tenantCode';
import { SetupRail } from './SetupRail';
import { SECTION_LABELS, SETUP_STEPS, parameterItems, type SetupStepId } from './stepRegistry';
import { defaultStep, useSetupState } from './useSetupState';
import { AuxInfoStep } from './steps/AuxInfoStep';
import { DataSyncStep } from './steps/DataSyncStep';
import { ItemHostStep } from './steps/ItemHostStep';
import { ParametersStep } from './steps/ParametersStep';
import { ProfileStep } from './steps/ProfileStep';
import { RunBoardStep } from './steps/RunBoardStep';

const CREATE_STEPS = [
  { id: 'basics', label: 'Basics' },
  { id: 'admin', label: 'Admin user' },
  { id: 'profile', label: 'Profile & languages' },
] as const;

/** Content column widths: forms read best narrow; grids/boards need room. */
const WIDE_STEPS: ReadonlySet<string> = new Set(['catalog', 'bookings', 'run']);

/** The context line an item-host step opens with. */
const STEP_INTROS: Partial<Record<SetupStepId, string>> = {
  partner:
    'A partner’s sandbox. Record the partner’s signed contract on its page first — every other item ' +
    'waits on it. Then seed the sample data and hand the sandbox over: the claim link mints the ' +
    'partner’s first developer key.',
  notifications:
    'Notifications are created switched off. Nothing is sent until they are enabled at soft launch.',
  feed:
    'The booking feed: the booking system calls us on every booking and order change, through ' +
    'notification subscriptions created in process mode, so bookings import as they change. Run ' +
    'creates them — only on a click or an armed setup run — and never deletes a subscription or ' +
    'switches its mode.',
};

interface Props {
  mode: 'create' | 'setup';
}

export function TourOperatorSetupPage({ mode }: Props) {
  return mode === 'create' ? <CreateMode /> : <SetupMode />;
}

// --------------------------------------------------------------------------- create
function CreateMode() {
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canCreatePartner = hasPerm('console_partners_manage');
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [stepIdx, setStepIdx] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);
  // The easy flow's "Advanced setup" hands over the identity it has so far (never a key).
  const location = useLocation();
  const [data, setData] = useState<WizardData>(() => initialWizardData(prefillFromState(location.state)));
  const [logo, setLogo] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const patch = (partial: Partial<WizardData>) => setData((prev) => ({ ...prev, ...partial }));

  useEffect(() => {
    headingRef.current?.focus();
  }, [stepIdx]);

  function validate(): string | null {
    if (stepIdx === 0) {
      if (!data.name.trim()) return 'Name is required.';
      if (!data.code.trim()) return 'Code is required.';
      if (!isTenantCode(data.code)) return TENANT_CODE_RULE;
      if (codeAvailable === null) return 'Checking code availability, please wait.';
      if (codeAvailable === false) return 'Code is already taken — choose a different one.';
      if (!logo) return 'Logo is required.';
    }
    if (stepIdx === 1) {
      if (!data.admin_email.trim()) return 'Admin email is required.';
      if (data.admin_password.length < 8) return 'Password must be at least 8 characters.';
      if (!data.admin_first_name.trim()) return 'First name is required.';
      if (!data.admin_last_name.trim()) return 'Last name is required.';
      // Blank rows are dropped at submit; anything filled in must be usable.
      const team = cleanTeamMembers(data.team_members);
      if (team.error) return team.error;
    }
    if (stepIdx === 2) {
      if (data.lang_enabled.length === 0) return 'At least one language must be enabled.';
    }
    return null;
  }

  function next() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setStepIdx((s) => {
      const n = s + 1;
      setMaxVisited((m) => Math.max(m, n));
      return n;
    });
  }

  async function submit() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Drop the rows the operator added and left empty — the backend rejects
      // the whole payload on a blank email, and an abandoned row is not an answer.
      const result = await topsApi.create(
        createPayload({ ...data, team_members: cleanTeamMembers(data.team_members).members }),
        logo,
      );
      snackbar.show(
        result.sandbox
          ? `Tour operator "${result.name}" (${result.code}) created, with its sandbox ${result.sandbox.code}.`
          : `Tour operator "${result.name}" (${result.code}) created.`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['tourOperators'] });
      // The whole point of the page: creation LANDS somewhere — on this
      // tenant's setup, at the first thing that needs a human.
      navigate(`/tour-operators/${result.id}/setup`, { replace: true });
    } catch (e: unknown) {
      setError(
        // A refused sandbox setting names its entry (`errors`); both tenants were rolled back.
        data.create_sandbox
          ? sandboxErrorMessage(e, 'Creation failed. Please try again.')
          : e instanceof ConsoleApiError
            ? (e.body?.detail ?? e.message)
            : 'Creation failed. Please try again.',
      );
      setSubmitting(false);
    }
  }

  const isLast = stepIdx === CREATE_STEPS.length - 1;

  return (
    <Box>
      <PageHeader helpStory="create-to-wizard" title="New tour operator" subtitle="Create, then configure — the setup continues on the same page." />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px minmax(0,1fr)' }, gap: 3 }}>
        <Box component="nav" aria-label="Setup steps" sx={{ alignSelf: 'start', position: { md: 'sticky' }, top: { md: 88 } }}>
          <List dense disablePadding>
            <Typography variant="overline" sx={{ display: 'block', px: 1.5, pt: 1, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary', lineHeight: '30px' }}>
              {SECTION_LABELS.create}
            </Typography>
            {CREATE_STEPS.map((s, i) => (
              <ListItemButton
                key={s.id}
                selected={i === stepIdx}
                disabled={i > maxVisited || submitting}
                onClick={() => {
                  if (i <= maxVisited) {
                    setError(null);
                    setStepIdx(i);
                  }
                }}
                aria-current={i === stepIdx ? 'step' : undefined}
                sx={{ borderRadius: 1, py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {i + 1}
                  </Typography>
                </ListItemIcon>
                <ListItemText primary={s.label} primaryTypographyProps={{ variant: 'body2', fontWeight: i === stepIdx ? 600 : 500 }} />
              </ListItemButton>
            ))}
            {/* What follows creation — visible so the journey is legible, locked
                until the tenant exists. */}
            {(['configure', 'data', 'run'] as const).map((section) => (
              <Box key={section}>
                <Typography variant="overline" sx={{ display: 'block', px: 1.5, pt: 1, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.disabled', lineHeight: '30px' }}>
                  {SECTION_LABELS[section]}
                </Typography>
                {SETUP_STEPS.filter((s) => s.section === section).map((s) => (
                  <ListItemButton key={s.id} disabled sx={{ borderRadius: 1, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <LockOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    </ListItemIcon>
                    <ListItemText primary={s.label} secondary="after creation" primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                  </ListItemButton>
                ))}
              </Box>
            ))}
          </List>
        </Box>

        <Box sx={{ maxWidth: 880 }}>
          <Typography ref={headingRef} tabIndex={-1} variant="h6" component="h2" sx={{ mb: 2, outline: 'none' }}>
            {CREATE_STEPS[stepIdx]?.label}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {stepIdx === 0 && canCreatePartner && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Onboarding an integration partner rather than a tour operator?{' '}
              <Link component={RouterLink} to="/tour-operators/new/partner">
                Create the partner and its sandbox
              </Link>{' '}
              instead.
            </Alert>
          )}
          {stepIdx === 0 && (
            <Step1BasicInfo data={data} logo={logo} onChange={patch} onLogoChange={setLogo} onCodeAvailability={setCodeAvailable} />
          )}
          {stepIdx === 1 && <Step2AdminUser data={data} onChange={patch} />}
          {stepIdx === 2 && <StepProfile data={data} onChange={patch} />}
          {stepIdx === 2 && <WizardSandboxSection data={data} onChange={patch} />}

          <Box sx={{ display: 'flex', gap: 1, mt: 3, pb: 4 }}>
            <Button onClick={() => setStepIdx((s) => s - 1)} disabled={stepIdx === 0 || submitting}>
              Back
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {isLast && data.create_sandbox && (
              <Box sx={{ alignSelf: 'center' }}>
                <PairedSummary code={data.code} withSandbox />
              </Box>
            )}
            {isLast ? (
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                onClick={() => void submit()}
                disabled={submitting}
              >
                {submitting ? 'Creating…' : 'Create & continue to setup'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={next}
                disabled={submitting || (stepIdx === 0 && codeAvailable !== true && data.code.length > 0)}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// --------------------------------------------------------------------------- setup
function SetupMode() {
  const params = useParams<{ id: string; step?: string }>();
  const topId = Number(params.id);
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_preflight_manage');
  const headingRef = useRef<HTMLHeadingElement>(null);

  const topQuery = useQuery({
    queryKey: ['tourOperators', 'detail', topId],
    queryFn: () => topsApi.retrieve(topId),
    enabled: Number.isFinite(topId),
  });
  const state = useSetupState(topId);
  const actions = usePreflightItemActions(topId);

  const stepIds = new Set<string>(SETUP_STEPS.map((s) => s.id));
  const urlStep = params.step && stepIds.has(params.step) ? (params.step as SetupStepId) : null;
  const activeStep: SetupStepId = urlStep ?? defaultStep(state.steps);

  // A bare /setup visit lands on the first step needing a human (URL-canonical,
  // so refresh/back keep working) — but only once the items have loaded.
  useEffect(() => {
    if (!urlStep && !state.itemsLoading && state.items.length > 0) {
      navigate(`/tour-operators/${topId}/setup/${defaultStep(state.steps)}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStep, state.itemsLoading]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [activeStep]);

  if (!Number.isFinite(topId)) {
    return <Alert severity="warning">Invalid tour operator id.</Alert>;
  }
  if (topQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (topQuery.error || !topQuery.data) {
    return <Alert severity="error">Couldn’t load tour operator.</Alert>;
  }
  const to = topQuery.data;
  // Only a wizard-created tenant has a checklist, and the whole page is a view
  // over that checklist — so for anyone else this URL names a real operator
  // with nothing to show. Say so at the URL that was asked for (the same shape
  // as the guards above, and as RequireAuth's permission notice) rather than
  // redirecting: a silent bounce off a link out of a ticket explains nothing.
  if (!to.onboarding_managed) {
    return (
      <Box>
        <PageHeader
          title={`Setup — ${to.name}`}
          titleText={to.name}
          subtitle={`Code: ${to.code}`}
          actions={
            <Button component={RouterLink} to={`/tour-operators/${to.id}`}>
              Open tenant
            </Button>
          }
        />
        <Alert severity="info">
          This operator was not created through the setup wizard, so it has no onboarding
          checklist and there is nothing to set up here. Open the tenant to work on it.
        </Alert>
      </Box>
    );
  }
  if (state.itemsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (state.itemsError) {
    return <Alert severity="error">Couldn’t load the setup checklist.</Alert>;
  }
  const activeDef = SETUP_STEPS.find((s) => s.id === activeStep);
  const itemsByAction = new Map(state.items.map((i) => [i.action, i]));
  const ownedItems = (activeDef?.itemActions ?? [])
    .map((a) => itemsByAction.get(a))
    .filter((i): i is NonNullable<typeof i> => Boolean(i && i.applicable));
  // The data steps embed ONE sync form (their first registered action); any further
  // owned item — the first-webhook verify row on Booking backfill — renders beneath
  // it as a generic panel, so the form is looked up by action, never by position.
  const dataSyncItem =
    activeStep === 'catalog' || activeStep === 'bookings' || activeStep === 'auxinfo'
      ? ownedItems.find((i) => i.action === activeDef?.itemActions[0])
      : undefined;
  const dataSideItems = ownedItems.filter((i) => i !== dataSyncItem);

  return (
    <Box>
      <PageHeader
        helpStory="setup-page"
        section={activeDef?.label}
        title={`Setup — ${to.name}`}
        titleAdornment={<EnvironmentBadge environment={to.environment} paired={to.sandbox != null} />}
        // The operator alone, for the ⌘K recents entry and the document title:
        // both sit next to a word for the page already.
        titleText={to.name}
        subtitle={`Code: ${to.code}`}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              variant="outlined"
              color={state.progress.done === state.progress.total ? 'success' : 'default'}
              label={`${state.progress.done} of ${state.progress.total} done`}
            />
            <Button component={RouterLink} to={`/tour-operators/${to.id}`}>
              Open tenant
            </Button>
            {canManage && (
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                disabled={state.runReadyPending}
                onClick={() => state.runReady()}
              >
                Run setup
              </Button>
            )}
          </Box>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px minmax(0,1fr)' }, gap: 3 }}>
        <Box sx={{ alignSelf: 'start', position: { md: 'sticky' }, top: { md: 88 } }}>
          <SetupRail
            steps={state.steps}
            activeId={activeStep}
            onSelect={(id) => navigate(`/tour-operators/${topId}/setup/${id}`)}
          />
        </Box>

        <Box sx={{ maxWidth: WIDE_STEPS.has(activeStep) ? 1200 : 880, pb: 4 }}>
          <Typography ref={headingRef} tabIndex={-1} variant="h6" component="h2" sx={{ mb: 2, outline: 'none' }}>
            {activeDef?.label}
          </Typography>

          {activeStep === 'profile' &&
            (state.profile ? (
              <ProfileStep topId={topId} profile={state.profile} items={state.items} canManage={canManage} />
            ) : (
              <CircularProgress size={20} />
            ))}

          {activeStep === 'parameters' && (
            <ParametersStep
              items={parameterItems(state.items)}
              topId={topId}
              toCode={to.code}
              canManage={canManage}
              actions={actions}
            />
          )}

          {activeStep === 'partner' && ownedItems.length > 0 && (
            <PartnerStepLink items={state.items} />
          )}

          {(activeStep === 'partner' ||
            activeStep === 'access' ||
            activeStep === 'notifications' ||
            activeStep === 'feed') && (
            <ItemHostStep
              items={ownedItems}
              topId={topId}
              toCode={to.code}
              canManage={canManage}
              actions={actions}
              intro={STEP_INTROS[activeStep]}
            />
          )}

          {(activeStep === 'catalog' || activeStep === 'bookings') &&
            (ownedItems.length > 0 ? (
              <>
                {activeStep === 'bookings' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Backfill fetches existing bookings without messaging guests — invitations and
                    surveys stay suppressed unless deliberately enabled.
                  </Alert>
                )}
                {dataSyncItem && (
                  <DataSyncStep
                    kind={activeStep}
                    item={dataSyncItem}
                    top={to}
                    topId={topId}
                    canManage={canManage}
                    actions={actions}
                  />
                )}
                {dataSideItems.length > 0 && (
                  <Box sx={{ mt: dataSyncItem ? 3 : 0 }}>
                    <ItemHostStep
                      items={dataSideItems}
                      topId={topId}
                      toCode={to.code}
                      canManage={canManage}
                      actions={actions}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">Not applicable to this operator.</Alert>
            ))}

          {activeStep === 'auxinfo' &&
            (dataSyncItem ? (
              <AuxInfoStep item={dataSyncItem} topId={topId} canManage={canManage} actions={actions} />
            ) : (
              <Alert severity="info">Not applicable to this operator.</Alert>
            ))}

          {activeStep === 'run' && (
            <RunBoardStep
              topId={topId}
              items={state.items}
              profile={state.profile}
              canManage={canManage}
              runReady={state.runReady}
              runReadyPending={state.runReadyPending}
              setAutoAdvance={state.setAutoAdvance}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

/** The Partner step's link to the partner's page, where the contract is recorded
 *  (the PARTNER_CONTRACT item names the partner in its detail). */
function PartnerStepLink({ items }: { items: PreflightItem[] }) {
  const contract = items.find((i) => i.action === 'PARTNER_CONTRACT');
  const partner = (contract?.detail as { partner?: { id: number; code: string; name: string } } | undefined)?.partner;
  if (!partner) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Button component={RouterLink} to={`/partners/${partner.id}`} variant="outlined" size="small">
        Open partner {partner.name} ({partner.code})
      </Button>
    </Box>
  );
}
